import React, { useEffect, useRef, useState } from 'react';
import { ChatMessage } from '@/src/types/Chat';
import { Box, Button, Link } from '@amzn/awsui-components-react';
import './JamChat.scss';
import { decodeHtmlCharCodes, getMessageSentTime } from '@/src/utils/chat.utils';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/src/utils/i18n.utils';
import Linkify from '../../../atoms/Linkify/Linkify';
import { useUser } from '@/src/store/user.context';

interface JamChatProps {
  messages: ChatMessage[];
  sendMessage: (message: string) => void;
  disabled: boolean;
  ended?: boolean;
  closeWindow: () => void;
  senderAvatar?: string;
  renderAfterMessages?: React.ReactNode;
  /**
   * Optional custom renderer for a message body. When provided, this replaces
   * the default Linkify-based plain text rendering (e.g. to render markdown
   * for AI assistant responses).
   */
  renderMessageBody?: (chatMessage: ChatMessage) => React.ReactNode;
  /**
   * When true, scroll so the top of the most recent message is at the top of
   * the visible chat area instead of scrolling to the bottom. This is useful
   * for long messages (e.g. AI assistant responses) so the user can read from
   * the beginning without having to scroll up.
   */
  scrollToLastMessageTop?: boolean;
}

const JamChat: React.FC<JamChatProps> = ({
  messages,
  sendMessage,
  disabled,
  ended = false,
  closeWindow,
  senderAvatar,
  renderAfterMessages,
  renderMessageBody,
  scrollToLastMessageTop = false,
}) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const messagesDivEl = useRef<HTMLDivElement>(null);
  const lastMessageEl = useRef<HTMLDivElement>(null);
  const messagesContainerEl = useRef<HTMLDivElement>(null);
  const { user } = useUser();

  const onMessageSend = () => {
    if (!message?.trim()) {
      return;
    }
    sendMessage(message);
    setMessage('');
  };

  const scrollToBottom = () => {
    const scrollToOptions = { behavior: 'instant' };
    messagesDivEl.current?.scrollIntoView(scrollToOptions as unknown as ScrollIntoViewOptions);
  };

  const scrollToLatestMessageTop = () => {
    const container = messagesContainerEl.current;
    const target = lastMessageEl.current;
    if (!container || !target) {
      return;
    }
    // If the latest message fits within the visible area, scroll all the way
    // to the bottom so any content rendered after it (e.g. suggested question
    // bubbles) stays visible. If the message is taller than the visible area,
    // pin its top to the top of the visible area so the user can read it from
    // the start without having to scroll up.
    const targetHeight = target.getBoundingClientRect().height;
    if (targetHeight <= container.clientHeight) {
      container.scrollTop = container.scrollHeight - container.clientHeight;
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    container.scrollTop += targetRect.top - containerRect.top;
  };

  useEffect(() => {
    // Defer until after the browser has applied the latest layout, so that any
    // content rendered alongside the new message (e.g. AI assistant suggested
    // question bubbles or markdown that finishes laying out after the initial
    // commit) is included in scrollHeight when we measure.
    const raf = window.requestAnimationFrame(() => {
      if (scrollToLastMessageTop) {
        scrollToLatestMessageTop();
      } else {
        scrollToBottom();
      }
    });
    return () => window.cancelAnimationFrame(raf);
  }, [messages, renderAfterMessages, scrollToLastMessageTop]);

  const renderMessage = (chatMessage: ChatMessage, index: number) => {
    const myMessage = user?.legacyLogin === chatMessage.senderParticipantId;
    const isLastMessage = index === (messages?.length ?? 0) - 1;
    return (
      <div
        className="message"
        key={`${chatMessage.timeSent}-${chatMessage.message}`}
        ref={isLastMessage ? lastMessageEl : undefined}
      >
        <div className={`message-item ${myMessage ? 'my-message' : ''}`}>
          <div className="username">
            {!myMessage && senderAvatar && <img src={senderAvatar} alt="" className="sender-avatar" />}
            {myMessage ? '' : chatMessage.sender}{' '}
            <span className="time">{getMessageSentTime(chatMessage.timeSent)}</span>
          </div>
          <div className="message-text">
            {renderMessageBody ? (
              renderMessageBody(chatMessage)
            ) : (
              <Linkify external>{decodeHtmlCharCodes(chatMessage.message)}</Linkify>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="conversation">
      <div className="messages">
        <div className="messages-inner" tabIndex={0} ref={messagesContainerEl}>
          {(messages || []).map(renderMessage)}
          {renderAfterMessages}
          <div ref={messagesDivEl} />
        </div>
      </div>
      {ended && (
        <Box textAlign="center" padding={{ top: 'm', bottom: 'm' }} color="text-status-info">
          {t(i18nKeys.jamChat.status.chatEnded)}
        </Box>
      )}
      {disabled && (
        <Box textAlign="center" padding={{ top: 'm', bottom: 'm' }} color="text-status-error" fontWeight="bold">
          <Button data-testid="chat-close-window-button" onClick={closeWindow}>
            {t(i18nKeys.jamChat.actions.closeWindow)}
          </Button>
        </Box>
      )}
      {!disabled && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onMessageSend();
          }}
        >
          <div className="chat-footer">
            <div className="chat-input">
              <input
                placeholder={t(i18nKeys.myJams.chat.form.messagePlaceholder)}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                data-testid="chat-message-input"
              />
            </div>
            <Button variant="primary" data-testid="chat-send-message-button" onClick={onMessageSend}>
              {t(i18nKeys.general.send)}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};

export default JamChat;
